/* ALTO brand layer: motion and small interactions. No dependencies. */
(function () {
  'use strict';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var designMode = document.documentElement.classList.contains('shopify-design-mode');

  /* ---------- Reveal on scroll ---------- */
  function initReveal(root) {
    var nodes = (root || document).querySelectorAll('[data-alto-reveal]:not(.is-visible)');
    if (!nodes.length) return;
    if (reduceMotion || designMode || !('IntersectionObserver' in window)) {
      nodes.forEach(function (n) { n.classList.add('is-visible'); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          io.unobserve(entry.target);
        }
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
    nodes.forEach(function (n) {
      // Anything already in view on load shows immediately, staggered by index.
      io.observe(n);
    });

    // Timeline line draws when its list is visible
    var lists = (root || document).querySelectorAll('.alto-timeline__list');
    lists.forEach(function (list) {
      var lo = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (e.isIntersecting) { list.classList.add('is-visible'); lo.disconnect(); }
        });
      }, { threshold: 0.2 });
      lo.observe(list);
    });
  }

  /* ---------- Section flow: blend each section in from the one above it ---------- */
  function initFlow() {
    var transparent = function (c) { return !c || c === 'rgba(0, 0, 0, 0)' || c === 'transparent'; };
    var items = [];
    var all = Array.prototype.slice.call(document.querySelectorAll('#MainContent .shopify-section, footer .shopify-section'));
    all.forEach(function (wrap) {
      if (wrap.querySelector('[data-alto-sticky]')) return; // the sticky bar is fixed, not part of the flow
      if (wrap.querySelector('.alto-marquee')) return; // the ticker keeps crisp edges
      if (getComputedStyle(wrap).display === 'none') return;
      var target = wrap.matches('.alto-section') ? wrap : (wrap.querySelector('.alto-section') || wrap);
      var bg = getComputedStyle(target).backgroundColor;
      if (transparent(bg)) {
        var inner = wrap.querySelector('[class*="color-scheme-"]');
        if (inner) bg = getComputedStyle(inner).backgroundColor;
      }
      if (transparent(bg)) return;
      items.push({ el: target, bg: bg, alto: target.matches('.alto-section') });
    });
    var mix = function (a, b) {
      var pa = a.match(/[\d.]+/g), pb = b.match(/[\d.]+/g);
      if (!pa || !pb) return a;
      return 'rgb(' + Math.round((+pa[0] + +pb[0]) / 2) + ', ' + Math.round((+pa[1] + +pb[1]) / 2) + ', ' + Math.round((+pa[2] + +pb[2]) / 2) + ')';
    };
    items.forEach(function (item, i) {
      var prev = i > 0 ? items[i - 1].bg : null;
      var nextItem = i < items.length - 1 ? items[i + 1] : null;
      var next = nextItem ? nextItem.bg : null;
      var el = item.el;
      if (prev && prev !== item.bg) el.style.setProperty('--alto-prev-bg', prev); else el.style.removeProperty('--alto-prev-bg');
      if (next && next !== item.bg) el.style.setProperty('--alto-next-bg', next); else el.style.removeProperty('--alto-next-bg');
      // seam line only between two ALTO sections (the footer paints its background beneath it and would show a dark line)
      if (item.alto && nextItem && nextItem.alto && next !== item.bg) el.style.setProperty('--alto-seam', mix(item.bg, next));
      else el.style.removeProperty('--alto-seam');
      if (!item.alto) el.classList.toggle('alto-flow-in', !!(prev && prev !== item.bg));
    });
  }

  /* The stock buy box: Horizon ids it shopify-section-<template>__main on JSON templates */
  function mainSection() {
    return document.getElementById('shopify-section-main') || document.querySelector('main .shopify-section[id$="__main"]');
  }

  /* ---------- Liquid highlight follows the pointer on glass cards ---------- */
  function initPointerGlow() {
    if (!window.matchMedia('(hover: hover)').matches) return;
    document.addEventListener('pointermove', function (e) {
      var card = e.target.closest && e.target.closest('.alto-card, .alto-dose, .alto-steps__item, .alto-flavor');
      if (!card) return;
      var r = card.getBoundingClientRect();
      card.style.setProperty('--alto-mx', ((e.clientX - r.left) / r.width * 100).toFixed(1) + '%');
      card.style.setProperty('--alto-my', ((e.clientY - r.top) / r.height * 100).toFixed(1) + '%');
    }, { passive: true });
  }

  /* ---------- Sticky CTA bar ---------- */
  function initSticky() {
    var bar = document.querySelector('[data-alto-sticky]');
    if (!bar) return;
    var sentinel = document.querySelector('[data-alto-sticky-sentinel]');
    var join = document.getElementById('join') || mainSection();
    var footer = document.querySelector('footer');
    if (!sentinel) { bar.hidden = true; return; }
    bar.hidden = false;
    document.body.classList.add('alto-has-sticky');
    var pastHero = false, overJoin = false, overFooter = false;
    function update() {
      var show = pastHero && !overJoin && !overFooter;
      bar.classList.toggle('is-active', show);
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.target === sentinel) pastHero = !e.isIntersecting && e.boundingClientRect.top < 0;
        if (e.target === join) overJoin = e.isIntersecting;
        if (e.target === footer) overFooter = e.isIntersecting;
      });
      update();
    }, { threshold: [0, 0.15] });
    io.observe(sentinel);
    if (join) io.observe(join);
    if (footer) io.observe(footer);
  }

  /* ---------- Founding form: flavor vote tags + smooth anchor ---------- */
  function initJoinForms() {
    document.querySelectorAll('.alto-join').forEach(function (form) {
      var tags = form.querySelector('[data-alto-tags]');
      if (!tags) return;
      var base = tags.getAttribute('data-base-tags') || '';
      form.querySelectorAll('[data-alto-flavor]').forEach(function (radio) {
        radio.addEventListener('change', function () {
          tags.value = base + (radio.checked ? ', ' + radio.value : '');
        });
      });
    });

    // Flavor "vote" links preselect the chip in the signup form and scroll to it
    document.querySelectorAll('[data-alto-pick-flavor]').forEach(function (link) {
      link.addEventListener('click', function () {
        var value = link.getAttribute('data-alto-pick-flavor');
        document.querySelectorAll('[data-alto-flavor][value="' + value + '"]').forEach(function (radio) {
          radio.checked = true;
          radio.dispatchEvent(new Event('change', { bubbles: true }));
        });
      });
    });

    // "Join the founding batch" inside the password dialog: close it, then land on the signup form
    document.querySelectorAll('[data-alto-focus-join]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var input = document.querySelector('.alto-join input[type="email"]');
        if (!input) return;
        setTimeout(function () {
          input.scrollIntoView({ block: 'center', behavior: reduceMotion ? 'auto' : 'smooth' });
          try { input.focus({ preventScroll: true }); } catch (e) { /* noop */ }
        }, 320);
      });
    });

    // Focus the success message after a submit so screen readers announce it
    var success = document.querySelector('[data-alto-focus]');
    if (success && window.location.hash === '#contact_form') {
      success.scrollIntoView({ block: 'center', behavior: reduceMotion ? 'auto' : 'smooth' });
      try { success.focus({ preventScroll: true }); } catch (e) { /* noop */ }
    }
  }

  /* ---------- Copy link buttons ---------- */
  function initCopy() {
    document.querySelectorAll('[data-alto-copy]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var text = btn.getAttribute('data-alto-copy');
        var label = btn.querySelector('span');
        var original = label ? label.textContent : '';
        function done() {
          if (label) label.textContent = btn.getAttribute('data-copied-label') || 'Copied';
          setTimeout(function () { if (label) label.textContent = original; }, 1800);
        }
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(text).then(done, done);
        } else {
          var ta = document.createElement('textarea');
          ta.value = text; document.body.appendChild(ta); ta.select();
          try { document.execCommand('copy'); } catch (e) { /* noop */ }
          document.body.removeChild(ta); done();
        }
      });
    });
  }

  /* ---------- FAQ: animate <details> open and close ---------- */
  function initFaq() {
    document.querySelectorAll('.alto-faq__item').forEach(function (details) {
      var summary = details.querySelector('summary');
      var content = details.querySelector('.alto-faq__a');
      if (!summary || !content || reduceMotion) return;
      var animating = false;
      summary.addEventListener('click', function (e) {
        e.preventDefault();
        if (animating) return;
        animating = true;
        if (details.open) {
          var h = content.offsetHeight;
          var anim = content.animate([{ height: h + 'px', opacity: 1 }, { height: '0px', opacity: 0 }], { duration: 260, easing: 'cubic-bezier(0.65,0,0.35,1)' });
          content.style.overflow = 'hidden';
          anim.onfinish = function () { details.open = false; content.style.overflow = ''; content.style.height = ''; animating = false; };
        } else {
          details.open = true;
          var target = content.offsetHeight;
          content.style.overflow = 'hidden';
          var anim2 = content.animate([{ height: '0px', opacity: 0 }, { height: target + 'px', opacity: 1 }], { duration: 360, easing: 'cubic-bezier(0.22,1,0.36,1)' });
          anim2.onfinish = function () { content.style.overflow = ''; animating = false; };
        }
      });
    });
  }

  /* ---------- Smooth in-page anchors (CTA → #join / #buy) ---------- */
  function initAnchors() {
    document.addEventListener('click', function (e) {
      var a = e.target.closest && e.target.closest('a[href^="#"]');
      if (!a) return;
      var id = a.getAttribute('href').slice(1);
      if (!id) return;
      var target = document.getElementById(id) || (id === 'shopify-section-main' ? mainSection() : null);
      if (!target) return;
      e.preventDefault();
      var headerOffset = parseInt(getComputedStyle(document.body).getPropertyValue('--header-group-height')) || 70;
      var top = target.getBoundingClientRect().top + window.scrollY - headerOffset + 1;
      window.scrollTo({ top: top, behavior: reduceMotion ? 'auto' : 'smooth' });
      history.replaceState(null, '', '#' + id);
      var input = target.querySelector('input[type="email"]');
      if (input) setTimeout(function () { try { input.focus({ preventScroll: true }); } catch (err) { /* noop */ } }, reduceMotion ? 0 : 650);
    });
  }

  /* ---------- Pre-launch: CTAs that point to #join when the section is on another page ---------- */
  function initCtaFallback() {
    var cfg = window.altoConfig || {};
    if (!cfg.prelaunch) return;
    if (document.getElementById('join')) return;
    document.querySelectorAll('[data-alto-cta][href="#join"]').forEach(function (a) {
      a.setAttribute('href', (cfg.joinUrl || '/pages/founding-members') + '#join');
    });
  }

  /* ---------- Product hero: gallery, purchase options, quantity ---------- */
  function money(cents) {
    var cfg = window.altoConfig || {};
    var fmt = cfg.moneyFormat || '${{amount}}';
    var amount = cents / 100;
    var str = (Math.round(amount * 100) / 100).toFixed(2).replace(/\.00$/, '');
    if (str.indexOf('.') === -1) return fmt.replace(/\{\{\s*amount[^}]*\}\}/, str);
    return fmt.replace(/\{\{\s*amount[^}]*\}\}/, (Math.round(amount * 100) / 100).toFixed(2));
  }
  function initGallery() {
    document.querySelectorAll('[data-alto-gallery]').forEach(function (g) {
      var items = g.querySelectorAll('[data-alto-gallery-item]');
      var thumbs = g.querySelectorAll('[data-alto-gallery-thumb]');
      if (!thumbs.length) return;
      thumbs.forEach(function (t) {
        t.addEventListener('click', function () {
          var i = t.getAttribute('data-alto-gallery-thumb');
          items.forEach(function (it) { var on = it.getAttribute('data-alto-gallery-item') === i; it.hidden = !on; it.classList.toggle('is-active', on); });
          thumbs.forEach(function (tt) { var on = tt === t; tt.classList.toggle('is-active', on); tt.setAttribute('aria-selected', on ? 'true' : 'false'); });
        });
      });
    });
  }
  function initBuy() {
    document.querySelectorAll('[data-alto-buy]').forEach(function (box) {
      var form = box.querySelector('[data-alto-buy-form]');
      var plan = box.querySelector('[data-alto-selling-plan]');
      var label = box.querySelector('[data-alto-buy-label]');
      var price = box.querySelector('[data-alto-buy-price]');
      var qty = box.querySelector('[data-alto-qty] input');
      var cfg = window.altoConfig || {};
      function current() {
        var r = box.querySelector('.alto-option input:checked');
        return r || null;
      }
      function refresh() {
        var r = current();
        if (!r) return;
        if (plan) plan.value = r.value || '';
        if (label && r.getAttribute('data-button')) label.textContent = r.getAttribute('data-button');
        var unit = parseInt(r.getAttribute('data-price'), 10) || 0;
        var n = qty ? Math.max(1, parseInt(qty.value, 10) || 1) : 1;
        if (price && unit) price.textContent = money(unit * n);
      }
      box.querySelectorAll('.alto-option input').forEach(function (r) {
        r.addEventListener('change', function () {
          box.querySelectorAll('[data-alto-option]').forEach(function (o) { o.classList.toggle('is-selected', o.contains(r)); });
          refresh();
        });
      });
      var stepper = box.querySelector('[data-alto-qty]');
      if (stepper && qty) {
        var min = parseInt(qty.min, 10) || 1, max = parseInt(qty.max, 10) || 9;
        stepper.querySelector('[data-alto-qty-minus]').addEventListener('click', function () { qty.value = Math.max(min, (parseInt(qty.value, 10) || 1) - 1); refresh(); });
        stepper.querySelector('[data-alto-qty-plus]').addEventListener('click', function () { qty.value = Math.min(max, (parseInt(qty.value, 10) || 1) + 1); refresh(); });
        qty.addEventListener('change', function () { qty.value = Math.min(max, Math.max(min, parseInt(qty.value, 10) || 1)); refresh(); });
      }
      refresh();
      if (cfg.phase === 'preorder' && form) {
        // A plain submit works for pay-now pre-orders; PreProduct's embed takes over the tagged button for its own flows.
        form.addEventListener('submit', function () {
          var btn = form.querySelector('[data-alto-buy-btn]');
          if (btn) btn.classList.add('is-busy');
        });
      }
    });
  }

  function init() {
    initFlow();
    initReveal();
    initPointerGlow();
    initSticky();
    initJoinForms();
    initCopy();
    initFaq();
    initAnchors();
    initCtaFallback();
    initGallery();
    initBuy();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  // Theme editor: re-run reveal and flow for sections that are added or reloaded
  document.addEventListener('shopify:section:load', function (e) { initReveal(e.target); initFlow(); });
  document.addEventListener('shopify:section:reorder', function () { initFlow(); });
})();
